import { useCallback, useEffect, useMemo, useState } from "react";
import useInteresadosList from "./useInteresadosList";
import useInteresadoForm from "./useInteresadoForm";
import fuentesOrigenService from "../../../../services/fuentesOrigenService";
import empresasService from "../../../../services/empresasService";
import { listarInteresados } from "../../../../services/interesadosService";
import { showToast } from "../../../../utils/showToast";

// Escalona la carga de catalogos/validacion respecto al listado paginado
// (que ya se dispara solo al montar useInteresadosList) para no sumar una
// rafaga de 4 peticiones simultaneas al abrir la pagina.
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function useInteresadosLogic() {
  const list = useInteresadosList();
  const [fuentes, setFuentes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  // Lista completa (sin paginar) solo para validar documentos duplicados al
  // crear/editar: no hay UNIQUE en la tabla interesados, asi que el chequeo
  // es en el cliente y necesita ver TODOS los interesados, no solo la pagina
  // actual de la tabla (que ahora esta paginada por rendimiento).
  const [existentes, setExistentes] = useState([]);

  const cargarExistentes = useCallback(async () => {
    try {
      const data = await listarInteresados();
      setExistentes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchCatalogos = async () => {
      try {
        const [fuentesResp, empresasResp] = await Promise.all([
          fuentesOrigenService.listar().catch(() => []),
          empresasService.listar().catch(() => []),
        ]);
        if (cancelled) return;
        setFuentes(Array.isArray(fuentesResp) ? fuentesResp : []);
        setEmpresas(Array.isArray(empresasResp) ? empresasResp : []);
      } catch (err) {
        console.error(err);
        showToast({ type: "warning", message: "No se pudo cargar las fuentes u empresas." });
      }
    };
    fetchCatalogos();
    wait(300).then(() => {
      if (!cancelled) cargarExistentes();
    });
    return () => {
      cancelled = true;
    };
  }, [cargarExistentes]);

  const fuentesOptions = useMemo(
    () =>
      fuentes
        .map((fuente) => {
          const value = fuente.id ?? fuente.value ?? null;
          if (value === null || value === undefined) return null;
          return {
            value: String(value),
            label: fuente.nombre || fuente.descripcion || fuente.codigo || String(value),
          };
        })
        .filter(Boolean),
    [fuentes]
  );

  const empresasOptions = useMemo(
    () =>
      empresas
        .map((empresa) => {
          const value = empresa.id ?? empresa.value ?? null;
          if (value === null || value === undefined) return null;
          return {
            value: String(value),
            label: empresa.nombre || empresa.razon_social || empresa.label || String(value),
          };
        })
        .filter(Boolean),
    [empresas]
  );

  const form = useInteresadoForm({
    onCreated: (nuevo) => {
      list.upsertInteresado(nuevo);
      cargarExistentes();
    },
    onUpdated: (actualizado) => {
      list.upsertInteresado(actualizado);
      if (list.seleccionado?.id === actualizado.id) list.setSeleccionado(actualizado);
      cargarExistentes();
    },
    onDeleted: (eliminado) => {
      list.removeInteresado(eliminado.id);
      if (list.seleccionado?.id === eliminado.id) list.setSeleccionado(null);
      cargarExistentes();
    },
    fuentesOptions,
    empresasOptions,
    existentes,
  });

  return {
    ...list,
    form,
    fuentesOptions,
    empresasOptions,
  };
}
